import { useState, useEffect } from 'react';

// Tailwind breakpoints
const BREAKPOINTS = {
  sm: '(min-width: 640px)',
  md: '(min-width: 768px)',
  lg: '(min-width: 1024px)',
  xl: '(min-width: 1280px)',
  mobile: '(max-width: 639px)',
  touch: '(pointer: coarse)',
} as const;

function computeMatches(queries: Record<string, MediaQueryList>) {
  const isMobile = queries.mobile.matches;
  const isSmUp = queries.sm.matches;
  const isMdUp = queries.md.matches;
  const isLgUp = queries.lg.matches;
  const isXlUp = queries.xl.matches;

  return {
    isMobile,
    isTablet: isSmUp && !isLgUp,
    isDesktop: isLgUp,
    isLargeDesktop: isXlUp,
    isTouchDevice: queries.touch.matches,
    breakpoint: {
      xs: isMobile,
      sm: isSmUp && !isMdUp,
      md: isMdUp && !isLgUp,
      lg: isLgUp && !isXlUp,
      xl: isXlUp,
    },
  };
}

const SSR_DEFAULTS = {
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  isLargeDesktop: true,
  isTouchDevice: false,
  breakpoint: { xs: false, sm: false, md: false, lg: true, xl: true },
};

/**
 * Hook for responsive breakpoint detection using matchMedia change events.
 * Uses Tailwind default breakpoints: sm (640), md (768), lg (1024), xl (1280)
 *
 * Only triggers re-renders when an actual breakpoint boundary is crossed,
 * not on every pixel of resize.
 */
export function useResponsive() {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return SSR_DEFAULTS;
    const queries = Object.fromEntries(
      Object.entries(BREAKPOINTS).map(([key, query]) => [key, window.matchMedia(query)])
    ) as Record<string, MediaQueryList>;
    return computeMatches(queries);
  });

  useEffect(() => {
    const queries = Object.fromEntries(
      Object.entries(BREAKPOINTS).map(([key, query]) => [key, window.matchMedia(query)])
    ) as Record<string, MediaQueryList>;

    const handler = () => setMatches(computeMatches(queries));

    // Listen for breakpoint crossings only — not every pixel of resize
    Object.values(queries).forEach((mql) => mql.addEventListener('change', handler));
    return () => {
      Object.values(queries).forEach((mql) => mql.removeEventListener('change', handler));
    };
  }, []);

  return matches;
}
